import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Loader2 } from "lucide-react";
import { useIntl } from "react-intl";

import { IntegrationChannelIcon } from "@/components/integrations/integration-channel-icon";
import { buildBrandPath } from "@/hooks/use-route-brand";
import { INSTAGRAM_INTEGRATION_CHANNEL } from "@/lib/integrations";
import { trpc } from "@/lib/trpc";
import { Badge } from "@runcheetah/ui";
import { Button } from "@runcheetah/ui";
import { Card, CardContent, CardDescription, CardTitle } from "@runcheetah/ui";
import { cn } from "@runcheetah/ui";

type ConnectionStatus = "connected" | "expired" | "revoked" | "error";

export function InstagramConnectionCard({
  brandId,
  onConnectionChange,
}: {
  brandId: string;
  onConnectionChange?: (isConnected: boolean) => void;
}) {
  const intl = useIntl();
  const utils = trpc.useUtils();
  const connectionQuery = trpc.socialConnection.getInstagram.useQuery(
    { brandId },
    {
      staleTime: 0,
      refetchOnWindowFocus: true,
    },
  );

  const [actionError, setActionError] = useState("");
  const [isRedirecting, setIsRedirecting] = useState(false);

  const disconnect = trpc.socialConnection.disconnectInstagram.useMutation({
    onSuccess: async () => {
      setActionError("");
      await utils.socialConnection.getInstagramImport.invalidate({ brandId });
      await utils.socialConnection.getInstagram.invalidate({ brandId });
    },
    onError: (error) => setActionError(error.message),
  });

  const onConnect = async () => {
    setActionError("");
    setIsRedirecting(true);
    try {
      const { url } = await utils.socialConnection.getInstagramAuthUrl.fetch({
        brandId,
        returnTo: `${window.location.origin}/app/brands/${brandId}/integrations/instagram`,
      });
      window.location.assign(url);
    } catch (error) {
      setIsRedirecting(false);
      setActionError(
        error instanceof Error
          ? error.message
          : intl.formatMessage({
              id: "integrations.instagram.authStartFailed",
              defaultMessage: "Failed to start Instagram auth.",
            }),
      );
    }
  };

  const connection = connectionQuery.data ?? null;
  const status: ConnectionStatus | null = connection?.status ?? null;
  const isConnected = status === "connected";
  const needsReconnect = status === "expired" || status === "revoked" || status === "error";

  const badgeVariant = isConnected ? "default" : needsReconnect ? "destructive" : "outline";
  const badgeLabel = status
    ? intl.formatMessage({
        id: `integrations.instagram.status.${status}`,
        defaultMessage:
          status === "connected"
            ? "Connected"
            : status === "expired"
              ? "Expired"
              : status === "revoked"
                ? "Revoked"
                : "Error",
      })
    : intl.formatMessage({
        id: "integrations.instagram.status.notConnected",
        defaultMessage: "Not connected",
      });
  const activeAuthFlow = connection?.authFlow ?? null;
  const detailHref = buildBrandPath(brandId, "/integrations/instagram");

  useEffect(() => {
    onConnectionChange?.(isConnected);
  }, [isConnected, onConnectionChange]);

  return (
    <Card className="flex min-h-48 flex-col rounded-2xl border-border/70 bg-card py-0 shadow-none">
      <CardContent className="flex flex-1 flex-col gap-5 p-4 text-sm sm:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <div
              className={cn(
                "flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border shadow-sm",
                INSTAGRAM_INTEGRATION_CHANNEL.accentClass,
              )}
            >
              <IntegrationChannelIcon channelId="instagram" className="h-6 w-6" />
            </div>
            <div className="min-w-0 flex-1 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <CardTitle className="text-base leading-5">
                  {intl.formatMessage(INSTAGRAM_INTEGRATION_CHANNEL.name)}
                </CardTitle>
              </div>
              <CardDescription className="text-sm leading-5">
                {intl.formatMessage(INSTAGRAM_INTEGRATION_CHANNEL.description)}
              </CardDescription>
            </div>
          </div>
          <Badge
            shape="pill"
            variant={badgeVariant}
            className={cn(
              "w-fit shrink-0 shadow-sm",
              badgeVariant === "outline" && "border-border/70 bg-muted/40 text-foreground",
            )}
          >
            {badgeLabel}
          </Badge>
        </div>

        {connectionQuery.isPending ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>
              {intl.formatMessage({
                id: "integrations.instagram.checkingConnection",
                defaultMessage: "Checking connection…",
              })}
            </span>
          </div>
        ) : connection ? (
          <div className="space-y-2 rounded-xl border border-border/70 bg-muted/20 p-3">
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-muted-foreground">
                {intl.formatMessage({
                  id: "integrations.instagram.details.account",
                  defaultMessage: "Account",
                })}
              </span>
              <span className="min-w-0 truncate text-right font-medium">
                {connection.accountName ??
                  intl.formatMessage({
                    id: "instagramIntegration.accountFallback",
                    defaultMessage: "Instagram account",
                  })}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-muted-foreground">
                {intl.formatMessage({
                  id: "integrations.instagram.details.method",
                  defaultMessage: "Method",
                })}
              </span>
              <span className="min-w-0 truncate text-right font-medium">
                {intl.formatMessage(
                  activeAuthFlow === "meta"
                    ? {
                        id: "integrations.instagram.authFlow.meta",
                        defaultMessage: "Meta Business",
                      }
                    : {
                        id: "integrations.instagram.authFlow.direct",
                        defaultMessage: "Instagram Login",
                      },
                )}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-muted-foreground">
                {intl.formatMessage({
                  id: "integrations.instagram.details.tokenExpires",
                  defaultMessage: "Token expires",
                })}
              </span>
              <span className="min-w-0 truncate text-right font-medium">
                {connection.expiresAt
                  ? intl.formatDate(new Date(connection.expiresAt), { dateStyle: "medium" })
                  : intl.formatMessage({
                      id: "integrations.instagram.never",
                      defaultMessage: "Never",
                    })}
              </span>
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-border/70 bg-muted/20 px-3 py-3 text-muted-foreground">
            {intl.formatMessage({
              id: "integrations.instagram.unlinkedNotice",
              defaultMessage:
                "No Instagram account is linked yet. Personal Instagram accounts cannot be connected.",
            })}
          </div>
        )}

        {connection ? (
          <div className="rounded-xl border border-border/70 bg-muted/20 px-3 py-3 text-muted-foreground">
            {intl.formatMessage({
              id: "integrations.instagram.connectedSummary",
              defaultMessage: "View account status and imported posts on the Instagram page.",
            })}
            {connection.lastError ? (
              <p className="mt-2 text-xs text-destructive">{connection.lastError}</p>
            ) : null}
          </div>
        ) : null}

        {!connection ? (
          <p className="text-sm text-muted-foreground">
            {intl.formatMessage(INSTAGRAM_INTEGRATION_CHANNEL.note)}
          </p>
        ) : null}

        <div className="mt-auto grid gap-2">
          {connection ? (
            <Button asChild className="w-full font-semibold">
              <Link to={detailHref}>
                {intl.formatMessage({
                  id: "integrations.instagram.open",
                  defaultMessage: "Open Instagram",
                })}
              </Link>
            </Button>
          ) : null}

          {isConnected ? (
            <Button
              variant="outline"
              className="w-full font-semibold"
              onClick={() => disconnect.mutate({ brandId })}
              disabled={disconnect.isPending}
            >
              {disconnect.isPending
                ? intl.formatMessage({
                    id: "integrations.instagram.disconnecting",
                    defaultMessage: "Disconnecting…",
                  })
                : intl.formatMessage({
                    id: "integrations.instagram.disconnect",
                    defaultMessage: "Disconnect",
                  })}
            </Button>
          ) : (
            <Button
              className="w-full font-semibold"
              onClick={() => void onConnect()}
              disabled={isRedirecting}
            >
              {isRedirecting
                ? intl.formatMessage({
                    id: "integrations.instagram.redirecting",
                    defaultMessage: "Redirecting…",
                  })
                : needsReconnect
                  ? intl.formatMessage({
                      id: "integrations.instagram.reconnectDirect",
                      defaultMessage: "Reconnect with Instagram Login",
                    })
                  : intl.formatMessage({
                      id: "integrations.instagram.connectDirect",
                      defaultMessage: "Connect with Instagram Login",
                    })}
            </Button>
          )}
          {needsReconnect ? (
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => disconnect.mutate({ brandId })}
              disabled={disconnect.isPending}
            >
              {disconnect.isPending
                ? intl.formatMessage({
                    id: "integrations.instagram.disconnecting",
                    defaultMessage: "Disconnecting…",
                  })
                : intl.formatMessage({
                    id: "integrations.instagram.remove",
                    defaultMessage: "Remove",
                  })}
            </Button>
          ) : null}
        </div>

        {actionError ? <p className="text-sm text-destructive">{actionError}</p> : null}
      </CardContent>
    </Card>
  );
}
